import { useMemo, useState } from 'react'
import {
  ALARMES_GT06_DE_BASE, ALARMES_PAR_DEFAUT, alarmesDeLaRegle, creerRegle, DEFAUTS_REGLE, DESCRIPTION_TYPE_REGLE, erreursRegle, LIBELLE_ALARME, LIBELLE_SEVERITE,
  LIBELLE_TYPE_REGLE, majRegle, parametresComplets, PASTILLE_SEVERITE, portee, REGLES_CONSEILLEES, resumeRegle, SCHEMA_PARAMETRES, SEVERITES, supprimerRegle,
  TYPES_REGLE, type Regle, type ReglagesAlertes, type SaisieRegle, type Severite, type TypeRegle,
} from '../../api/alertes'
import { nomBoitier } from '../../api/gps'
import type { DonneesOps } from '../../api/types'
import { useDroits } from '../Contexte'
import { Icone } from '../Icone'
import { ListeDeroulante } from '../ListeDeroulante'
import { Modale } from '../Modale'
import { basculer, ecrire } from './AlertesOutils'

/** Onglet « Règles » : une ligne par règle, avec son interrupteur, ses seuils, sa portée et ses canaux. */
export function OngletRegles({
  regles,
  reglages,
  donnees,
  orgId,
  gere,
  chargement,
  surNotifier,
  surChangement,
}: {
  regles: readonly Regle[]
  reglages: ReglagesAlertes
  donnees: DonneesOps
  orgId: string
  gere: boolean
  chargement: boolean
  surNotifier: (message: string) => void
  surChangement: () => Promise<void>
}) {
  const { apercu } = useDroits()
  const [edite, setEdite] = useState<Regle | 'nouvelle' | null>(null)
  const noms = useMemo(() => new Map(donnees.boitiers.map((b) => [b.id, nomBoitier(b, donnees.vehicules)])), [donnees.boitiers, donnees.vehicules])
  const pour = { apercu, surNotifier }

  async function interrupteur(r: Regle) {
    const ok = await ecrire(pour, () => majRegle(r.id, { actif: !r.actif }), r.actif ? 'Règle désactivée : ses alertes ouvertes sont refermées' : 'Règle activée')
    if (ok) await surChangement()
  }
  async function retirer(r: Regle) {
    if (!window.confirm(`Supprimer la règle « ${r.nom} » ?\n\nSes alertes ouvertes sont refermées ; l’historique est conservé.`)) return
    const ok = await ecrire(pour, () => supprimerRegle(r.id), 'Règle supprimée')
    if (ok) await surChangement()
  }
  async function creerConseillees() {
    const existants = new Set(regles.filter((r) => r.deviceIds === null).map((r) => r.type))
    const ok = await ecrire(
      pour,
      async () => {
        for (const s of REGLES_CONSEILLEES) if (!existants.has(s.type)) await creerRegle(orgId, s)
      },
      'Règles conseillées créées',
    )
    if (ok) await surChangement()
  }

  return (
    <>
      <div className="card" data-anim>
        <div className="card-head">
          <div>
            <h3>
              <Icone nom="gear" classe="h-ic" /> Règles d’alerte
            </h3>
            <div className="ch-sub">
              Chaque règle vaut pour tout le parc ou pour certains boîtiers (la règle d’un boîtier remplace celle du parc pour lui). L’interrupteur éteint = la
              règle n’existe plus.
            </div>
          </div>
          <span className="sp" />
          {gere && (
            <button className="btn btn-gold btn-sm" onClick={() => setEdite('nouvelle')}>
              <Icone nom="plus" /> Nouvelle règle
            </button>
          )}
        </div>
        <div className="card-body">
          {chargement ? (
            <div className="no-hit">Chargement…</div>
          ) : regles.length === 0 ? (
            <div className="no-hit" style={{ padding: 18 }}>
              <b>Aucune règle.</b>
              <br />
              Sans règle, aucune alerte ne se déclenche. Commencez par les règles conseillées (limite de vitesse, boîtier hors ligne, alarmes du boîtier) ; vous
              réglerez ensuite les seuils.
              {gere && (
                <div className="nh-actions">
                  <button className="btn btn-gold btn-sm" onClick={() => void creerConseillees()}>
                    Créer les règles conseillées
                  </button>
                </div>
              )}
            </div>
          ) : (
            <ul className="liste-regles">
              {regles.map((r) => (
                <li key={r.id} className={r.actif ? 'regle' : 'regle eteinte'}>
                  <label className="interrupteur interrupteur-seul" title={r.actif ? 'Désactiver la règle' : 'Activer la règle'}>
                    <input
                      type="checkbox"
                      checked={r.actif}
                      disabled={!gere}
                      onChange={() => void interrupteur(r)}
                      aria-label={`${r.actif ? 'Désactiver' : 'Activer'} la règle ${r.nom}`}
                    />
                    <span className="bascule" aria-hidden="true" />
                  </label>
                  <div className="corps">
                    <div className="nom">
                      {r.nom} <span className="t-muted">· {LIBELLE_TYPE_REGLE[r.type]}</span>
                    </div>
                    <div className="t-muted">{resumeRegle(r)}</div>
                    <div className="etiquettes">
                      <span className="badge plain b-gray">{portee(r, noms)}</span>
                      <span className={`badge ${PASTILLE_SEVERITE[r.severite]}`}>{LIBELLE_SEVERITE[r.severite]}</span>
                      <span className="badge plain b-gold">Historique</span>
                      {r.canaux.email && <span className="badge plain b-gold">E-mail</span>}
                      {r.canaux.telegram && <span className="badge plain b-gold">Telegram</span>}
                    </div>
                  </div>
                  {gere && (
                    <div className="actions-ligne">
                      <button className="btn btn-outline btn-sm" onClick={() => setEdite(r)}>
                        Modifier
                      </button>
                      <button className="btn btn-outline btn-sm" style={{ color: 'var(--err)' }} onClick={() => void retirer(r)} aria-label={`Supprimer la règle ${r.nom}`} title="Supprimer">
                        <Icone nom="trash" />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="card" data-anim>
        <div className="card-head">
          <h3>
            <Icone nom="alert" classe="h-ic" /> Ce que nos boîtiers savent signaler
          </h3>
        </div>
        <div className="card-body">
          <p className="t-muted" style={{ fontSize: 13 }}>
            La vitesse, les zones, l’arrêt prolongé et le « hors ligne » sont calculés par SwiftDeliver à partir des positions : ils marchent avec tout boîtier. Les{' '}
            <b>alarmes du boîtier</b> dépendent de son modèle : un boîtier GT06 de base remonte surtout{' '}
            {ALARMES_GT06_DE_BASE.map((a) => LIBELLE_ALARME[a]?.toLowerCase()).join(', ')} ; le freinage brusque, l’accélération brusque ou la collision
            n’existent que sur certains modèles. La « vitesse dangereuse » est une <b>estimation</b> (décélération et changement de cap entre deux positions
            consécutives) : elle demande une position toutes les 10 à 20 secondes (commande « Intervalle » de la fiche du boîtier).
          </p>
        </div>
      </div>

      {edite && (
        <ModaleRegle
          regle={edite === 'nouvelle' ? null : edite}
          reglages={reglages}
          donnees={donnees}
          orgId={orgId}
          surNotifier={surNotifier}
          surFermer={() => setEdite(null)}
          surEnregistre={async () => {
            setEdite(null)
            await surChangement()
          }}
        />
      )}
    </>
  )
}

function ModaleRegle({
  regle,
  reglages,
  donnees,
  orgId,
  surNotifier,
  surFermer,
  surEnregistre,
}: {
  regle: Regle | null
  reglages: ReglagesAlertes
  donnees: DonneesOps
  orgId: string
  surNotifier: (message: string) => void
  surFermer: () => void
  surEnregistre: () => Promise<void>
}) {
  const { apercu } = useDroits()
  const [type, setType] = useState<TypeRegle>(regle?.type ?? 'exces_vitesse')
  const [nom, setNom] = useState(regle?.nom ?? DEFAUTS_REGLE[type].nom)
  const [nomModifie, setNomModifie] = useState(!!regle)
  const [actif, setActif] = useState(regle?.actif ?? true)
  const [severite, setSeverite] = useState<Severite>(regle?.severite ?? DEFAUTS_REGLE[type].severite)
  const [parc, setParc] = useState(regle ? regle.deviceIds === null : true)
  const [ids, setIds] = useState<string[]>(regle?.deviceIds ?? [])
  const [params, setParams] = useState<Record<string, number | boolean>>(() => parametresComplets(regle?.type ?? type, regle?.parametres ?? {}))
  const [alarmes, setAlarmes] = useState<string[]>(regle ? alarmesDeLaRegle(regle) : [...ALARMES_PAR_DEFAUT])
  const [email, setEmail] = useState(regle?.canaux.email ?? false)
  const [telegram, setTelegram] = useState(regle?.canaux.telegram ?? false)
  const [erreurs, setErreurs] = useState<string[]>([])
  const [envoi, setEnvoi] = useState(false)

  function changerType(t: TypeRegle) {
    setType(t)
    setParams(parametresComplets(t, {}))
    setSeverite(DEFAUTS_REGLE[t].severite)
    if (!nomModifie) setNom(DEFAUTS_REGLE[t].nom)
  }

  async function valider() {
    const parametres: Record<string, unknown> = { ...params }
    if (type === 'alarme') parametres.alarmes = alarmes
    const pb = erreursRegle(type, nom, parametres)
    if (!parc && ids.length === 0) pb.push('Choisissez au moins un boîtier, ou « Tout le parc ».')
    setErreurs(pb)
    if (pb.length > 0) return
    const saisie: SaisieRegle = { type, nom, actif, deviceIds: parc ? null : ids, parametres, severite, canaux: { email, telegram } }
    setEnvoi(true)
    const ok = await ecrire({ apercu, surNotifier }, () => (regle ? majRegle(regle.id, saisie) : creerRegle(orgId, saisie)), regle ? 'Règle enregistrée' : 'Règle créée')
    setEnvoi(false)
    if (ok) await surEnregistre()
  }

  return (
    <Modale
      titre={regle ? `Modifier la règle « ${regle.nom} »` : 'Nouvelle règle d’alerte'}
      ouverte
      surFermer={surFermer}
      surEnregistrer={() => void valider()}
      enCours={envoi}
      large
    >
      {erreurs.length > 0 && (
        <div className="bandeau erreur" role="alert">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {erreurs.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="form-grid form-alertes">
        <div className="field form-full">
          <label>Type de règle</label>
          <ListeDeroulante
            pleineLargeur
            valeur={type}
            desactive={!!regle}
            surChangement={(v) => changerType(v as TypeRegle)}
            options={TYPES_REGLE.map((t) => ({ valeur: t, libelle: LIBELLE_TYPE_REGLE[t] }))}
          />
          <div className="t-muted" style={{ marginTop: 4 }}>{DESCRIPTION_TYPE_REGLE[type]}</div>
        </div>
        <div className="field form-full">
          <label htmlFor="regle-nom">Nom de la règle</label>
          <input
            id="regle-nom"
            value={nom}
            maxLength={80}
            onChange={(e) => {
              setNom(e.target.value)
              setNomModifie(true)
            }}
          />
        </div>

        <fieldset className="form-full groupe-champs">
          <legend>Boîtiers concernés</legend>
          <label className="case-oc">
            <input type="radio" name="portee" checked={parc} onChange={() => setParc(true)} />
            <span>Tout le parc</span>
          </label>
          <label className="case-oc">
            <input type="radio" name="portee" checked={!parc} onChange={() => setParc(false)} />
            <span>Certains boîtiers seulement (leur règle remplace celle du parc)</span>
          </label>
          {!parc && (
            <div className="choix-boitiers">
              {donnees.boitiers.length === 0 && <span className="t-muted">Aucun boîtier.</span>}
              {donnees.boitiers.map((b) => (
                <label className="case-oc" key={b.id}>
                  <input type="checkbox" checked={ids.includes(b.id)} onChange={() => setIds(basculer(ids, b.id))} />
                  <span>{nomBoitier(b, donnees.vehicules)}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        {SCHEMA_PARAMETRES[type].map((c) =>
          c.booleen ? (
            <label className="case-oc form-full" key={c.cle}>
              <input type="checkbox" checked={params[c.cle] === true} onChange={(e) => setParams({ ...params, [c.cle]: e.target.checked })} />
              <span>
                <b>{c.libelle}</b>
                <span className="t-muted"> — {c.aide}</span>
              </span>
            </label>
          ) : (
            <div className="field" key={c.cle}>
              <label htmlFor={`p-${c.cle}`}>{`${c.libelle} (${c.unite})`}</label>
              <input
                id={`p-${c.cle}`}
                type="number"
                inputMode="decimal"
                min={c.min}
                max={c.max}
                step={1}
                value={Number.isFinite(params[c.cle] as number) ? (params[c.cle] as number) : ''}
                onChange={(e) => setParams({ ...params, [c.cle]: e.target.value === '' ? Number.NaN : Number(e.target.value) })}
              />
              <div className="t-muted">{`${c.aide} Entre ${c.min} et ${c.max}.`}</div>
            </div>
          ),
        )}

        {type === 'alarme' && (
          <fieldset className="form-full groupe-champs">
            <legend>Alarmes surveillées</legend>
            <p className="t-muted" style={{ margin: '0 0 8px' }}>
              Cochez celles qui doivent déclencher une alerte. Un boîtier n’en remonte qu’une partie (un GT06 de base :{' '}
              {ALARMES_GT06_DE_BASE.map((a) => LIBELLE_ALARME[a]?.toLowerCase()).join(', ')}).
            </p>
            <div className="choix-boitiers">
              {Object.entries(LIBELLE_ALARME).map(([code, libelle]) => (
                <label className="case-oc" key={code}>
                  <input type="checkbox" checked={alarmes.includes(code)} onChange={() => setAlarmes(basculer(alarmes, code))} />
                  <span>{libelle}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        <div className="field">
          <label>Gravité</label>
          <ListeDeroulante
            pleineLargeur
            valeur={severite}
            surChangement={(v) => setSeverite(v as Severite)}
            options={SEVERITES.map((s) => ({ valeur: s, libelle: LIBELLE_SEVERITE[s] }))}
          />
          <div className="t-muted">« Urgent » ressort en rouge dans l’historique et dans la pastille de la navigation.</div>
        </div>
        <label className="case-oc" style={{ alignSelf: 'center' }}>
          <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} />
          <span>Règle active</span>
        </label>

        <fieldset className="form-full groupe-champs">
          <legend>Prévenir par</legend>
          <label className="case-oc">
            <input type="checkbox" checked disabled readOnly />
            <span>
              Historique <span className="t-muted">(toujours)</span>
            </span>
          </label>
          <label className="case-oc">
            <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
            <span>
              E-mail{' '}
              <span className="t-muted">
                — {reglages.emails.length > 0 ? `à ${reglages.emails.join(', ')}` : 'aux propriétaires et dispatchers (réglable dans « Notifications »)'}
              </span>
            </span>
          </label>
          <label className="case-oc">
            <input type="checkbox" checked={telegram} onChange={(e) => setTelegram(e.target.checked)} />
            <span>
              Telegram{' '}
              <span className="t-muted">
                —{' '}
                {reglages.telegramChatId
                  ? reglages.telegramVerifie
                    ? 'conversation vérifiée'
                    : 'conversation à vérifier (onglet « Notifications »)'
                  : 'à brancher dans l’onglet « Notifications »'}
              </span>
            </span>
          </label>
          <p className="t-muted" style={{ margin: '6px 0 0' }}>
            Une alerte est notifiée une seule fois, à sa création : ses répétitions ne renvoient rien. Délai d’envoi : une minute au plus.
          </p>
        </fieldset>
      </div>
    </Modale>
  )
}
